import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode'; // Correct import for jwt-decode
import infoIcon from '../assets/icons/Info.svg';
// Define interface for the Report Issue API response
interface ReportIssueData {
    id: number;
    reportContent: string | null;
    subject: string;
    userId: number;
    createdDate: string;
    userName: string;
    email: string;
    phoneNumber: number;
}

interface CustomJwtPayload {
    RoleId: string; // Treat RoleId as string
}

// Add this skeleton loader component
const ReportIssueSkeleton = () => (
    <div className="animate-pulse">
        {/* Date Picker Skeleton */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                    <div className="h-4 w-20 bg-gray-200 rounded"></div>
                    <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
                </div>
                <div className="space-y-2">
                    <div className="h-4 w-20 bg-gray-200 rounded"></div>
                    <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
                </div>
                <div className="flex items-end">
                    <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
                </div>
            </div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
            {/* Header Skeleton */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 grid grid-cols-6 gap-6 border-b">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-5 bg-gray-200 rounded"></div>
                ))}
            </div>

            {/* Rows Skeleton */}
            <div className="divide-y divide-gray-100">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="p-4 grid grid-cols-6 gap-6">
                        {[...Array(6)].map((_, j) => (
                            <div key={j} className="space-y-2">
                                <div className="h-4 bg-gray-200 rounded w-full"></div>
                                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const ViewReportIssue: React.FC = () => {
    const navigate = useNavigate();
    const [reportIssueData, setReportIssueData] = useState<ReportIssueData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const lastToastTimeRef = useRef<number>(0);
    const TOAST_THROTTLE_MS = 3000; // 3 seconds
    useEffect(() => {
        const token = localStorage.getItem('token');
        // console.log('Retrieved token:', token);
        if (!token) {
            // console.log("No auth token found.");
            toast.error("No auth token found. Please log in.");
            navigate('/');
        } else {
            try {
                const decodedToken = jwtDecode<CustomJwtPayload>(token);
                // console.log("Decoded Token:", decodedToken);
                if (decodedToken.RoleId !== "1") { // Compare RoleId as string
                    // console.log("Unauthorized access: RoleId is not 1");
                    toast.error("Unauthorized access: RoleId is not 1");
                    navigate('/');
                }
            } catch (error) {
                console.error('Error decoding token:', error);
                toast.error("Invalid token. Please log in again.");
                navigate('/');
            }
        }
    }, [navigate]);

    // Fetch report issue data
    const fetchReportIssueData = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No auth token found');
            }

            const response = await fetch('https://pamsapi.vofoxsolutions.com/api/ReportIssue/(Admin)', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch report issue data: ${response.status}`);
            }

            const data: ReportIssueData[] = await response.json();
            // console.log("Fetched Report Issue Data:", data);
            setReportIssueData(data);
        } catch (error) {
            console.error('Error fetching report issue data:', error);
            // toast.error("An error occurred while fetching report issue data.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchReportIssueByDateRange = async () => {
        if (!startDate || !endDate) {
            if (!toast.isActive("select-dates")) {
                toast.info("Please select a start date and end date.", { toastId: "select-dates" });
            }
            return;
        }
        const formattedStartDate = new Date(startDate).toISOString().split('T')[0]; // yyyy-mm-dd
        const formattedEndDate = new Date(endDate).toISOString().split('T')[0];     // yyyy-mm-dd

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No auth token found');
            }
            const response = await fetch(`https://pamsapi.vofoxsolutions.com/api/ReportIssue/by-date-range?startDate=${formattedStartDate}&endDate=${formattedEndDate}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch report issue data by date range: ${response.status}`);
            }
            const data: ReportIssueData[] = await response.json();
          if(data.length === 0){
            if(
                !toast.isActive("no-data") && 
                Date.now() - lastToastTimeRef.current > TOAST_THROTTLE_MS 
            ){
                toast.info("No data found for the selected date range.", { toastId: "no-data" });
                lastToastTimeRef.current = Date.now();
            }
        }
            setReportIssueData(data);
        } catch (error) {
            console.error('Error fetching report issue data by date range:', error);
            // toast.error("An error occurred while fetching report issue data by date range.");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle fetching data when the page loads
    useEffect(() => {
        fetchReportIssueData();
    }, []);

    const handleReportIssueClick = (index: number) => {
        navigate(`/report-issue-details/${index}`, { 
            state: { reportIssueData: reportIssueData[index] } 
        });
    };

    // Handle start date change
    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        
        // If end date exists and is before new start date, clear it
        if (endDate && endDate < newStartDate) {
            setEndDate('');
        }
    };

    // Handle end date change
    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEndDate = e.target.value;
        setEndDate(newEndDate);
    };

    return (
        <div className="h-screen overflow-hidden bg-magnolia select-none">
            <div className="flex flex-col h-screen">
                <header className="flex items-center justify-between px-8 py-4 bg-white/90 backdrop-blur-sm shadow-sm">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                            <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
                            Manage Report Issues
                        </h1>
                    </div>
                </header>

                <main className="flex-1 overflow-x-hidden overflow-y-auto px-8 py-6">
                    <div className="max-w-7xl mx-auto">
                        {isLoading ? (
                            <ReportIssueSkeleton />
                        ) : (
                            <>
                                <div className="flex items-center justify-between mb-8">
                                    <h4 className="text-xl text-gray-800 font-semibold flex items-center gap-3">
                                        Report Issues Management
                                        {reportIssueData.length > 0 && (
                                            <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                                                {reportIssueData.length} Records
                                            </span>
                                        )}
                                    </h4>
                                </div>

                                {/* Date Picker Section */}
                                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Start Date</label>
                                            <input
                                                type="date"
                                                value={startDate}
                                                onChange={handleStartDateChange}
                                                max={new Date().toISOString().split('T')[0]} // Prevent future dates
                                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">End Date</label>
                                            <input
                                                type="date"
                                                value={endDate}
                                                onChange={handleEndDateChange}
                                                min={startDate} // Disable dates before start date
                                                max={new Date().toISOString().split('T')[0]} // Prevent future dates
                                                disabled={!startDate} // Disable until start date is selected
                                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                onClick={fetchReportIssueByDateRange}
                                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-6 rounded-lg
                                                         hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                Fetch Reports
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Report Issue Data Table */}
                                {reportIssueData.length > 0 && (
                                    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
                                        {/* Table Header */}
                                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 grid grid-cols-6 gap-6 border-b">
                                            {["User Name", "Email", "Phone Number", "Created Date", "Subject", "Report Content"].map((header) => (
                                                <div key={header} className="text-sm font-semibold text-gray-600 text-center">
                                                    {header}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Table Body */}
                                        <div className="divide-y divide-gray-100">
                                            {reportIssueData.map((report, index) => (
                                                <div 
                                                    key={report.id} 
                                                    className="p-4 grid grid-cols-6 gap-6 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150"
                                                    onClick={() => handleReportIssueClick(index)}
                                                >
                                                    <div className="w-full px-2 break-words">
                                                        <span className="text-sm font-semibold line-clamp-3 hover:line-clamp-none">
                                                            {report.userName}
                                                        </span>
                                                    </div>
                                                    <div className="w-full px-2 break-words">
                                                        <span className="text-sm font-semibold line-clamp-3 hover:line-clamp-none">
                                                            {report.email}
                                                        </span>
                                                    </div>
                                                    <div className="w-full px-2 break-words">
                                                        <span className="text-sm font-semibold">
                                                            {report.phoneNumber}
                                                        </span>
                                                    </div>
                                                    <div className="w-full px-2 break-words">
                                                        <span className="text-sm font-semibold">
                                                            {report.createdDate}
                                                        </span>
                                                    </div>
                                                    <div className="w-full px-2 break-words">
                                                        <span className="text-sm font-semibold line-clamp-3 hover:line-clamp-none">
                                                            {report.subject || 'No subject'}
                                                        </span>
                                                    </div>
                                                    <div className="w-full px-2 break-words">
                                                        <span className="text-sm font-semibold line-clamp-3 hover:line-clamp-none">
                                                            {report.reportContent || 'No content'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </main>

                <ToastContainer 
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    pauseOnHover
                    draggable={false}
                    theme="light"
                />
            </div>
        </div>
    );
};

export default ViewReportIssue;
